import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
    Button, 
    Grid, 
    Row, 
    Col, 
    Table, 
    Label, 
    FormControl,
    DropdownButton,
    MenuItem,
    OverlayTrigger,
    Tooltip,
    Panel,
    Glyphicon,
    ButtonGroup,
    Checkbox
} from "react-bootstrap";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { 
    getOrders, 
    updateOrderStatus,
    getAllStripeCustomers,
    getAllStripeSubscriptions,
    getCapsAdminOnly,
    getBuilder,
    postNewOrder,
    patchCapTracking,
    updateOrder
} from "../utils/bedrockAPI";
import { getUser } from "../utils/Common";
import OrderEditModal from "../components/OrderEditModal";
import ShippingConfirmationModal from "../components/ShippingConfirmationModal";
import "./Orders.css";
import "./Admin.css";

// Global cache for expensive API calls - persists across component re-renders
const globalCache = {
    stripeCustomers: null,
    stripeSubscriptions: null,
    lastFetchTime: null,
    cacheTimeout: 5 * 60 * 1000 // 5 minutes cache timeout
};

const ORDER_STATUSES_EXCLUDED_FROM_TOTALS = ['RETURNED', 'RETURNING', 'DELETE', 'LOST'];

export default function Orders(props) {
    const [orders, setOrders] = useState([]);
    const [potentialOrders, setPotentialOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingStripe, setLoadingStripe] = useState(false);
    const [loadingPotential, setLoadingPotential] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        totalOrders: 0,
        activeOrders: 0,
        trialingOrders: 0,
        monthlyRevenue: 0,
        yearlyRevenue: 0,
        jobSightCount: 0,
        proCount: 0,
        legacyCount: 0
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [filterBillingStatus, setFilterBillingStatus] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('deliveryDate');
    const [sortDirection, setSortDirection] = useState('desc');
    const [isMounted, setIsMounted] = useState(true);
    const [allStripeCustomers, setAllStripeCustomers] = useState({});
    const [allStripeSubscriptions, setAllStripeSubscriptions] = useState({});
    const [usingCachedData, setUsingCachedData] = useState(false);
    const [expandedOrders, setExpandedOrders] = useState(new Set());
    const [showDeletedOrders, setShowDeletedOrders] = useState(false);
    const [showReturnedNoSubscriptionOrders, setShowReturnedNoSubscriptionOrders] = useState(false);
    const [editingTracking, setEditingTracking] = useState(null);
    const [trackingEditValue, setTrackingEditValue] = useState('');
    const [selectedOrders, setSelectedOrders] = useState(new Set());
    const [showShippingModal, setShowShippingModal] = useState(false);

    const orderStatuses = [
        'PENDING',
        'SUBMITTED', 
        'SHIPPING',
        'SHIPPED',
        'ARRIVED',
        'INSTALLED',
        'PROMOTIONAL',
        'RETURNING',
        'RETURNED',
        'REPLACEMENT',
        'DELETE'
    ];

    const billingStatuses = [
        'ALL',
        'ACTIVE',
        'TRIALING',
        'PAST DUE',
        'CANCELED',
        'NOT STARTED',
        'NO_SUBSCRIPTION'
    ];

    const statusColors = {
        'PENDING': 'warning',
        'SUBMITTED': 'info',
        'SHIPPING': 'warning',
        'SHIPPED': 'warning',
        'ARRIVED': 'warning',
        'INSTALLED': 'success',
        'PROMOTIONAL': 'info',
        'RETURNING': 'warning',
        'RETURNED': 'danger',
        'REPLACEMENT': 'info',
        'DELETE': 'danger'
    };

    useEffect(() => {
        loadOrders();
        return () => { 
            setIsMounted(false);
            // setLoadingEnrichment(false); // Ensure loading state is reset on unmount
        };
    }, []);

    const loadCachedStripeData = async () => {
        const now = Date.now();
        
        // Check if we have valid cached data
        if (globalCache.stripeCustomers && 
            globalCache.stripeSubscriptions && 
            globalCache.lastFetchTime && 
            (now - globalCache.lastFetchTime) < globalCache.cacheTimeout) {
            
            console.log('Using cached Stripe data');
            return {
                customers: globalCache.stripeCustomers,
                subscriptions: globalCache.stripeSubscriptions,
                fromCache: true
            };
        }
        
        // Cache is invalid or expired, fetch fresh data
        console.log('Fetching fresh Stripe data');
        const [stripeCustomersData, stripeSubscriptionsData] = await Promise.all([
            getAllStripeCustomers(),
            getAllStripeSubscriptions()
        ]);
        
        // Update global cache
        globalCache.stripeCustomers = stripeCustomersData;
        globalCache.stripeSubscriptions = stripeSubscriptionsData;
        globalCache.lastFetchTime = now;
        
        return {
            customers: stripeCustomersData,
            subscriptions: stripeSubscriptionsData,
            fromCache: false
        };
    };

    const loadOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Load orders first (this should be fast)
            const ordersData = await getOrders(500);
            
            // Set orders immediately so the table can render
            setOrders(ordersData);
            
            // Calculate basic stats without Stripe data
            calculateBasicStats(ordersData);
            
            // Load potential orders after orders are loaded
            loadPotentialOrders(ordersData);
            
            // Mark orders as loaded
            setLoading(false);
            
            // Now load Stripe data in the background
            loadStripeDataInBackground(ordersData);
            
        } catch (err) {
            setError('Failed to load orders');
            console.error('Error loading orders:', err);
            setLoading(false);
        }
    };

    const loadStripeDataInBackground = async (ordersData) => {
        try {
            setLoadingStripe(true);
            
            // Load cached/fresh Stripe data
            const stripeData = await loadCachedStripeData();
            const { customers: stripeCustomersData, subscriptions: stripeSubscriptionsData, fromCache } = stripeData;
            
            if (!isMounted) return; // Component unmounted, don't update state
            
            // Process Stripe customers into a lookup object
            const customersLookup = {};
            if (stripeCustomersData && stripeCustomersData.customers && Array.isArray(stripeCustomersData.customers)) {
                stripeCustomersData.customers.forEach(customer => {
                    if (customer.id) {
                        customersLookup[customer.id] = customer;
                    }
                });
            }
            setAllStripeCustomers(customersLookup);
            
            // Process Stripe subscriptions into a lookup object
            const subscriptionsLookup = {};
            if (stripeSubscriptionsData && stripeSubscriptionsData.subscriptions && Array.isArray(stripeSubscriptionsData.subscriptions)) {
                stripeSubscriptionsData.subscriptions.forEach(subscription => {
                    if (subscription.id) {
                        if (!subscriptionsLookup[subscription.customer]) {
                            subscriptionsLookup[subscription.customer] = [];
                        }
                        subscriptionsLookup[subscription.customer].push(subscription);
                    }
                });
            }
            setAllStripeSubscriptions(subscriptionsLookup);
            setUsingCachedData(fromCache);
            
            // Populate subscription objects for each order
            const ordersWithSubscriptions = ordersData.map(order => {
                let subscription = null;
                if (order.builder?.stripeId && subscriptionsLookup[order.builder.stripeId]) {
                    subscription = subscriptionsLookup[order.builder.stripeId].find(sub => sub.id === order.subscriptionId);
                }
                return {
                    ...order,
                    subscription
                };
            });
            
            if (!isMounted) return; // Component unmounted, don't update state
            
            setOrders(ordersWithSubscriptions);
            
            // Calculate full stats with Stripe data
            calculateStats(ordersWithSubscriptions, customersLookup, subscriptionsLookup);
            
        } catch (err) {
            console.error('Error loading Stripe data:', err);
            // Don't set error state for Stripe loading failures - orders are still usable
        } finally {
            setLoadingStripe(false);
        }
    };

    const loadPotentialOrders = async (existingOrders) => {
        try {
            setLoadingPotential(true);
            
            // Get all caps
            const capsData = await getCapsAdminOnly();
            
            // Create a set of cap IDs that already have orders (excluding deleted orders)
            const orderedCapIds = new Set(existingOrders.filter(order => order.status !== 'DELETE').map(order => order.capId));
            
            // Filter caps to find potential orders
            let potential = capsData.filter(cap => 
                // Cap has BUILD or ACTIVE status
                (cap.status === 'BUILD' || cap.status === 'ACTIVE') &&
                // Cap has a builder_id
                cap.builderId &&
                // Cap doesn't already have an order
                !orderedCapIds.has(cap.capId)
            );
            for (const cap of potential) {
                cap.builder = await getBuilder(cap.builderId);
            }
            
            // console.log('potential:', potential);
            setPotentialOrders(potential);
        } catch (err) {
            console.error('Failed to load potential orders:', err);
        } finally {
            setLoadingPotential(false);
        }
    };

    const createOrderFromCap = async (cap) => {
        try {
            // Create a basic order object from the cap
            const newOrderPayload = {
                capId: cap.capId,
                builderId: cap.builderId,
                status: 'BUILD',
            };

            const created = await postNewOrder(newOrderPayload);
            // API may return single order or array (e.g. bulk create)
            const createdOrder = Array.isArray(created) ? created[0] : created;
            if (!createdOrder || (createdOrder.orderId === undefined && createdOrder.id === undefined)) {
                console.error('Create order API did not return order with id:', created);
                return;
            }
            // Normalize: backend may return id, UI uses orderId
            const orderId = createdOrder.orderId ?? createdOrder.id;
            const fullOrder = {
                ...createdOrder,
                orderId: orderId,
                builder: cap.builder,
                cap: cap,
            };
            if (fullOrder.builder?.stripeId && allStripeSubscriptions[fullOrder.builder.stripeId]) {
                fullOrder.subscription = allStripeSubscriptions[fullOrder.builder.stripeId].find(sub => sub.id === fullOrder.subscriptionId);
            }

            setOrders(prev => {
                const next = [...prev, fullOrder];
                calculateStats(next, allStripeCustomers, allStripeSubscriptions);
                return next;
            });

            setPotentialOrders(prev => prev.filter(potentialCap => potentialCap.capId !== cap.capId));
        } catch (err) {
            console.error('Failed to create order from cap:', err);
        }
    };



    const calculateBasicStats = (orders) => {
        let jobSightCount = 0;
        let proCount = 0;
        let legacyCount = 0;
        
        const ordersForTotals = orders.filter(
            order => !ORDER_STATUSES_EXCLUDED_FROM_TOTALS.includes(order.status)
        );
        
        ordersForTotals.forEach(order => {
            // Skip orders with no camera assigned or decommissioned cap
            if (order.capId === null || !order.cap || order.cap.status === 'DECOMISSIONED') {
                return;
            }
            
            // Calculate product type
            let productType = 'Legacy';
            if (order.cap?.architecture?.endsWith('2CAM')) {
                productType = 'JobSight';
            } else if (order.cap?.architecture?.endsWith('4CAM')) {
                productType = 'PRO';
            }
            
            // Count product types
            if (productType === 'JobSight') {
                jobSightCount++;
            } else if (productType === 'PRO') {
                proCount++;
            } else {
                legacyCount++;
            }
        });
        
        setStats({
            totalOrders: ordersForTotals.length,
            activeOrders: 0, // Will be updated when Stripe data loads
            trialingOrders: 0, // Will be updated when Stripe data loads
            monthlyRevenue: 0, // Will be updated when Stripe data loads
            yearlyRevenue: 0, // Will be updated when Stripe data loads
            jobSightCount,
            proCount,
            legacyCount
        });
    };

    const calculateStats = (orders, stripeCustomers = {}, stripeSubscriptions = {}) => {
        let totalRevenue = 0;
        let activeOrders = 0;
        let trialingOrders = 0;
        let jobSightCount = 0;
        let proCount = 0;
        let legacyCount = 0;
        
        const ordersForTotals = orders.filter(
            order => !ORDER_STATUSES_EXCLUDED_FROM_TOTALS.includes(order.status)
        );
        
        orders.filter(order => order.status !== 'DELETE').forEach(order => {
            const subscription = order.subscription;
            let monthlyAmount = 0;
            if (order.rate) {
                monthlyAmount = order.rate * 100;
            } else if (subscription) {
                monthlyAmount = subscription.items?.data?.[0]?.price?.unit_amount || 0;
            }
            totalRevenue += monthlyAmount;
            if (subscription?.status === 'active') {
                activeOrders++;
            } else if (subscription?.status === 'trialing') {
                trialingOrders++;
            }
        });
        
        ordersForTotals.forEach(order => {
            let productType = null;
            if (order.capId !== null && order.cap && order.cap.status !== 'DECOMISSIONED') {
                if (order.cap?.architecture?.endsWith('2CAM')) {
                    productType = 'JobSight';
                } else if (order.cap?.architecture?.endsWith('4CAM')) {
                    productType = 'PRO';
                } else {
                    productType = 'Legacy';
                }
            }
            if (productType === 'JobSight') {
                jobSightCount++;
            } else if (productType === 'PRO') {
                proCount++;
            } else if (productType === 'Legacy') {
                legacyCount++;
            }
        });
        
        setStats({
            totalOrders: ordersForTotals.length,
            activeOrders,
            trialingOrders,
            monthlyRevenue: totalRevenue,
            yearlyRevenue: totalRevenue * 12,
            jobSightCount,
            proCount,
            legacyCount
        });
    };

    const updateSingleOrder = async (updatedOrderData) => {
        // Update the order directly in the orders array
        setOrders(prev => {
            const existingOrder = prev.find(order => order.orderId === updatedOrderData.orderId);
            
            if (existingOrder) {
                // Merge the existing data with the updates
                const mergedOrder = {
                    ...existingOrder,
                    // Apply the updates from the modal
                    status: updatedOrderData.status !== undefined ? updatedOrderData.status : existingOrder.status,
                    deliveryDate: updatedOrderData.deliveryDate !== undefined ? updatedOrderData.deliveryDate : existingOrder.deliveryDate,
                    notes: updatedOrderData.notes !== undefined ? updatedOrderData.notes : existingOrder.notes,
                    subscriptionId: updatedOrderData.subscriptionId !== undefined ? updatedOrderData.subscriptionId : existingOrder.subscriptionId,
                    purchaserId: updatedOrderData.purchaserId !== undefined ? updatedOrderData.purchaserId : existingOrder.purchaserId,
                    rate: updatedOrderData.rate !== undefined ? updatedOrderData.rate : existingOrder.rate,
                    addDate: updatedOrderData.addDate !== undefined ? updatedOrderData.addDate : existingOrder.addDate,
                    poNumber: updatedOrderData.poNumber !== undefined ? updatedOrderData.poNumber : existingOrder.poNumber
                };
                
                // Populate subscription if available
                if (mergedOrder.builder?.stripeId && allStripeSubscriptions[mergedOrder.builder.stripeId]) {
                    mergedOrder.subscription = allStripeSubscriptions[mergedOrder.builder.stripeId].find(sub => sub.id === mergedOrder.subscriptionId);
                }
                
                const updatedOrders = prev.map(order => 
                    order.orderId === updatedOrderData.orderId ? mergedOrder : order
                );
                
                // Recalculate stats with the updated order
                calculateStats(updatedOrders, allStripeCustomers, allStripeSubscriptions);
                
                return updatedOrders;
            }
            
            return prev;
        });
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            await updateOrderStatus(orderId, newStatus);
            
            // Update local state
            setOrders(prev => 
                prev.map(order => 
                    order.orderId === orderId 
                        ? { ...order, status: newStatus }
                        : order
                )
            );
            
            // Recalculate stats
            const updatedOrders = orders.map(order => 
                order.orderId === orderId 
                    ? { ...order, status: newStatus }
                    : order
            );
            
            const pendingOrders = updatedOrders.filter(order => 
                ['PENDING', 'SUBMITTED'].includes(order.status)
            ).length;
            const shippedOrders = updatedOrders.filter(order => 
                ['SHIPPED', 'ARRIVED', 'INSTALLED'].includes(order.status)
            ).length;
            
            setStats(prev => ({
                ...prev,
                pendingOrders,
                shippedOrders
            }));
        } catch (err) {
            setError('Failed to update order status');
            console.error('Error updating order status:', err);
        }
    };

    const handleTrackingUpdate = async (orderId, newTracking) => {
        try {
            // Find the order to get the capId
            const order = orders.find(o => o.orderId === orderId);
            if (!order || !order.capId) {
                throw new Error('Order or cap not found');
            }

            // Update the cap's tracking number through the API
            await patchCapTracking(order.capId, newTracking);
            
            // Update local state
            setOrders(prev => 
                prev.map(order => 
                    order.orderId === orderId 
                        ? { 
                            ...order, 
                            cap: { 
                                ...order.cap, 
                                tracking: newTracking 
                            } 
                        }
                        : order
                )
            );
            
            // Exit edit mode
            setEditingTracking(null);
            setTrackingEditValue('');
            
        } catch (err) {
            setError('Failed to update tracking number');
            console.error('Error updating tracking number:', err);
        }
    };

    const startTrackingEdit = (orderId, currentTracking) => {
        setEditingTracking(orderId);
        setTrackingEditValue(currentTracking || '');
    };

    const cancelTrackingEdit = () => {
        setEditingTracking(null);
        setTrackingEditValue('');
    };

    const handleRemoveCapId = async (orderId) => {
        try {
            // Update the order to set capId to null
            await updateOrder(orderId, { capId: null });
            
            // Update local state
            setOrders(prev => 
                prev.map(order => 
                    order.orderId === orderId 
                        ? { 
                            ...order, 
                            capId: null,
                            cap: null  // Clear the cap object as well
                        }
                        : order
                )
            );
            
        } catch (err) {
            setError('Failed to remove camera from order');
            console.error('Error removing capId from order:', err);
        }
    };

    const handleOrderSelection = (orderId, isSelected) => {
        setSelectedOrders(prev => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(orderId);
            } else {
                newSet.delete(orderId);
            }
            return newSet;
        });
    };

    const handleSelectAll = (isSelected) => {
        const filteredOrders = getFilteredOrders();
        if (isSelected) {
            const allOrderIds = new Set(filteredOrders.map(order => order.orderId));
            setSelectedOrders(allOrderIds);
        } else {
            setSelectedOrders(new Set());
        }
    };

    const handleShippingConfirmation = () => {
        if (selectedOrders.size === 0) return;
        setShowShippingModal(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    const formatCurrency = (amount) => {
        if (!amount) return '$0';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount / 100); // Assuming amount is in cents
    };

    const getMonthlyRate = (subscription, order) => {
        // Prioritize order.rate if it exists and is not null/undefined
        if (order?.rate !== null && order?.rate !== undefined) {
            return formatCurrency(order.rate * 100);
        }
        
        // Fall back to subscription rate if order rate is not available
        if (subscription && subscription.status !== 'not_started') {
            return formatCurrency(subscription.items?.data?.[0]?.price?.unit_amount || 0);
        }
        
        // Show loading indicator if Stripe data is still loading
        if (loadingStripe) {
            return (
                <span>
                    <Glyphicon glyph="refresh" className="spinning" style={{ fontSize: '12px', marginRight: '3px' }} />
                    Loading...
                </span>
            );
        }
        
        // Default to $0 if neither exists
        return formatCurrency(0);
    };

    const getBillingStatus = (subscription) => {
        if (!subscription) {
            // Show loading indicator if Stripe data is still loading
            if (loadingStripe) {
                return (
                    <span>
                        <Glyphicon glyph="refresh" className="spinning" style={{ fontSize: '12px', marginRight: '3px' }} />
                        Loading...
                    </span>
                );
            }
            return 'No Subscription';
        }
        
        if (subscription.status === 'trialing' && subscription.trial_end) {
            const now = Math.floor(Date.now() / 1000); // Current time in Unix timestamp
            const trialEnd = subscription.trial_end;
            const daysRemaining = Math.ceil((trialEnd - now) / (24 * 60 * 60)); // Convert seconds to days
            
            if (daysRemaining > 0) {
                return `TRIALING (${daysRemaining} days left)`;
            } else if (daysRemaining === 0) {
                return 'TRIALING (ends today)';
            } else {
                return 'TRIALING (expired)';
            }
        }
        let status = subscription.status.toUpperCase();
        if (status === 'PAST_DUE') {
            return 'PAST DUE';
        }
        if (status === 'NOT_STARTED') {
            return 'NOT STARTED';
        }
        
        return status;
    };

    const getShippingCity = (stripeCustomer) => {
        if (!stripeCustomer?.shipping?.address?.city) return 'N/A';
        return stripeCustomer.shipping.address.city + ', ' + stripeCustomer.shipping.address.state;
    };

    const getCameraStatus = (cap) => {
        if (!cap) return { text: 'Loading...', style: 'default' };
        
        if (cap.status === 'BUILD') {
            return { text: 'Unactivated', style: 'warning' };
        } else if (cap.status === 'ACTIVE') {
            if (cap.health === 'ONLINE') {
                return { text: 'Online', style: 'success' };
            } else if (cap.health === 'POWER_LOSS') {
                return { text: 'Power Loss', style: 'danger' };
            } else if (cap.health === 'ERROR') {
                return { text: 'Error', style: 'warning' };
            } else if (cap.health === 'BATTERY') {
                return { text: 'Battery', style: 'warning' };
            } else {
                return { text: cap.health || 'Unknown', style: 'default' };
            }
        } else if (cap.status === 'DECOMISSIONED') {
            return { text: 'Decommissioned', style: 'danger' };
        } else {
            return { text: cap.status || 'Unknown', style: 'default' };
        }
    };

    const toggleOrderExpansion = (orderId) => {
        setExpandedOrders(prev => {
            const newSet = new Set(prev);
            if (newSet.has(orderId)) {
                newSet.delete(orderId);
            } else {
                newSet.add(orderId);
            }
            return newSet;
        });
    };

    /** Returned with no Stripe subscription object; while Stripe is loading, keep rows visible if the order has a subscriptionId. */
    const isReturnedWithoutSubscription = (order) => {
        if (order.status !== 'RETURNED') return false;
        if (order.subscription) return false;
        if (loadingStripe && order.subscriptionId) return false;
        return true;
    };

    const getFilteredOrders = () => {
        // First filter out deleted orders (unless specifically filtering for DELETE status or showDeletedOrders is true)
        let filtered = (filterStatus === 'DELETE' || showDeletedOrders) ? orders : orders.filter(order => order.status !== 'DELETE');

        // Filter by order status
        if (filterStatus !== 'ALL') {
            filtered = filtered.filter(order => order.status === filterStatus);
        }

        // Filter by billing status
        if (filterBillingStatus !== 'ALL') {
            filtered = filtered.filter(order => {
                const billingStatus = getBillingStatus(order.subscription);
                if (filterBillingStatus === 'NO_SUBSCRIPTION') {
                    return billingStatus === 'No Subscription';
                }
                // Check if the billing status starts with the filter value
                return billingStatus.startsWith(filterBillingStatus);
            });
        }

        // Filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(order => 
                (order.cap?.name && order.cap.name.toLowerCase().includes(term)) ||
                (order.builder?.name && order.builder.name.toLowerCase().includes(term)) ||
                (order.purchaser?.email && order.purchaser.email.toLowerCase().includes(term)) ||
                (order.orderId && order.orderId.toString().includes(term)) ||
                (order.capId && order.capId.toString().includes(term)) ||
                (order.builderId && order.builderId.toString().includes(term))
            );
        }

        if (!showReturnedNoSubscriptionOrders) {
            filtered = filtered.filter(order => !isReturnedWithoutSubscription(order));
        }

        // Sort the filtered results
        // console.log('Sorting by:', sortBy, 'direction:', sortDirection);
        filtered.sort((a, b) => {
            let aValue, bValue;
            
            // Newly created orders (status BUILD) always pin to the top when sorting by delivery date
            if (sortBy === 'deliveryDate') {
                const aIsBuild = a.status === 'BUILD';
                const bIsBuild = b.status === 'BUILD';
                if (aIsBuild !== bIsBuild) return aIsBuild ? -1 : 1;
            }
            
            switch (sortBy) {
                case 'builder':
                    aValue = a.builder?.name || '';
                    bValue = b.builder?.name || '';
                    break;
                case 'orderId':
                    aValue = a.orderId || 0;
                    bValue = b.orderId || 0;
                    break;
                case 'status':
                    aValue = a.status || '';
                    bValue = b.status || '';
                    break;
                case 'rate':
                    aValue = a.rate || 0;
                    bValue = b.rate || 0;
                    break;
                case 'addDate':
                    aValue = new Date(a.addDate || 0);
                    bValue = new Date(b.addDate || 0);
                    break;
                case 'deliveryDate':
                    // Orders without a delivery date always sort last, regardless of direction
                    if (!a.deliveryDate && !b.deliveryDate) return 0;
                    if (!a.deliveryDate) return 1;
                    if (!b.deliveryDate) return -1;
                    aValue = new Date(a.deliveryDate);
                    bValue = new Date(b.deliveryDate);
                    break;
                default:
                    aValue = a[sortBy] || '';
                    bValue = b[sortBy] || '';
            }
            
            // console.log(`Comparing: ${aValue} vs ${bValue}`);
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return filtered;
    };

    const getFilteredRevenue = () => {
        const filteredOrders = getFilteredOrders();
        let totalRevenue = 0;
        
        filteredOrders.forEach(order => {
            // Skip deleted orders in revenue calculation
            if (order.status === 'DELETE') return;
            
            // Calculate monthly revenue for each filtered order
            let monthlyAmount = 0;
            if (order.rate) {
                monthlyAmount = order.rate * 100;
            } else if (order.subscription) {
                monthlyAmount = order.subscription.items?.data?.[0]?.price?.unit_amount || 0;
            }
            totalRevenue += monthlyAmount;
        });
        
        return totalRevenue;
    };

    const handleSort = (column) => {
        // console.log('handleSort called with column:', column);
        // console.log('Current sortBy:', sortBy, 'sortDirection:', sortDirection);
        
        if (sortBy === column) {
            const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            // console.log('Toggling direction to:', newDirection);
            setSortDirection(newDirection);
        } else {
            // console.log('Setting new sort column:', column, 'direction: asc');
            setSortBy(column);
            setSortDirection('asc');
        }
    };

    const renderStats = () => {
        const filteredRevenue = getFilteredRevenue();
        const hasFilters = filterStatus !== 'ALL' || filterBillingStatus !== 'ALL' || searchTerm;
        
        return (
            <div>
                <Row className="stats-row">
                    <Col md={2}>
                        <Panel className="stat-panel">
                            <h3>{stats.totalOrders}</h3>
                            <p>Total Orders</p>
                        </Panel>
                    </Col>
                    <Col md={2}>
                        <Panel className="stat-panel">
                            <h3>
                                {loadingStripe ? (
                                    <span>
                                        <Glyphicon glyph="refresh" className="spinning" style={{ fontSize: '14px', marginRight: '5px' }} />
                                        ...
                                    </span>
                                ) : (
                                    stats.activeOrders
                                )}
                            </h3>
                            <p>Active Orders</p>
                        </Panel>
                    </Col>
                    <Col md={2}>
                        <Panel className="stat-panel">
                            <h3>
                                {loadingStripe ? (
                                    <span>
                                        <Glyphicon glyph="refresh" className="spinning" style={{ fontSize: '14px', marginRight: '5px' }} />
                                        ...
                                    </span>
                                ) : (
                                    stats.trialingOrders
                                )}
                            </h3>
                            <p>Trialing Orders</p>
                        </Panel>
                    </Col>
                    <Col md={3}>
                        <Panel className="stat-panel">
                            <h3>
                                {loadingStripe ? (
                                    <span>
                                        <Glyphicon glyph="refresh" className="spinning" style={{ fontSize: '14px', marginRight: '5px' }} />
                                        Loading...
                                    </span>
                                ) : (
                                    formatCurrency(stats.monthlyRevenue)
                                )}
                            </h3>
                            <p>Monthly Revenue</p>
                        </Panel>
                    </Col>
                    <Col md={3}>
                        <Panel className="stat-panel">
                            <h3>
                                {loadingStripe ? (
                                    <span>
                                        <Glyphicon glyph="refresh" className="spinning" style={{ fontSize: '14px', marginRight: '5px' }} />
                                        Loading...
                                    </span>
                                ) : (
                                    formatCurrency(stats.yearlyRevenue)
                                )}
                            </h3>
                            <p>Yearly Revenue</p>
                        </Panel>
                    </Col>
                </Row>
                {hasFilters && (
                    <Row className="stats-row">
                        <Col md={12}>
                            <Panel className="stat-panel" bsStyle="info">
                                <h3>
                                    {loadingStripe ? (
                                        <span>
                                            <Glyphicon glyph="refresh" className="spinning" style={{ fontSize: '14px', marginRight: '5px' }} />
                                            Loading...
                                        </span>
                                    ) : (
                                        formatCurrency(filteredRevenue)
                                    )}
                                </h3>
                                <p>
                                    Filtered Monthly Revenue 
                                    {filterStatus !== 'ALL' && ` (${filterStatus} orders)`}
                                    {filterBillingStatus !== 'ALL' && ` (${filterBillingStatus === 'NO_SUBSCRIPTION' ? 'No Subscription' : filterBillingStatus} billing)`}
                                    {searchTerm && ` (search: "${searchTerm}")`}
                                </p>
                            </Panel>
                        </Col>
                    </Row>
                )}
                <Row className="stats-row">
                    <Col md={4}>
                        <Panel className="stat-panel">
                            <h3>{stats.jobSightCount}</h3>
                            <p>JobSight Cameras</p>
                        </Panel>
                    </Col>
                    <Col md={4}>
                        <Panel className="stat-panel">
                            <h3>{stats.proCount}</h3>
                            <p>PRO Cameras</p>
                        </Panel>
                    </Col>
                    <Col md={4}>
                        <Panel className="stat-panel">
                            <h3>{stats.legacyCount}</h3>
                            <p>Legacy Cameras</p>
                        </Panel>
                    </Col>
                </Row>
            </div>
        );
    };

    const renderFilters = () => (
        <Row className="filters-row">
            <Col md={3}>
                <FormControl
                    type="text"
                    placeholder="Search orders..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </Col>
            <Col md={2}>
                <FormControl
                    componentClass="select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                >
                    <option value="ALL">All Order Statuses</option>
                    {orderStatuses.map(status => (
                        <option key={status} value={status}>
                            {status}
                        </option>
                    ))}
                </FormControl>
            </Col>
            <Col md={2}>
                <FormControl
                    componentClass="select"
                    value={filterBillingStatus}
                    onChange={(e) => setFilterBillingStatus(e.target.value)}
                >
                    <option value="ALL">All Billing Statuses</option>
                    {billingStatuses.slice(1).map(status => (
                        <option key={status} value={status}>
                            {status === 'NO_SUBSCRIPTION' ? 'No Subscription' : status}
                        </option>
                    ))}
                </FormControl>
            </Col>
            <Col md={2}>
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id="clear-filters-tooltip">
                            Clear all filters
                        </Tooltip>
                    }
                >
                    <Button 
                        bsStyle="info" 
                        onClick={() => {
                            setSearchTerm('');
                            setFilterStatus('ALL');
                            setFilterBillingStatus('ALL');
                        }}
                        style={{ padding: '6px 8px', marginRight: '8px' }}
                    >
                        <Glyphicon glyph="remove" />
                    </Button>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id="refresh-data-tooltip">
                            Refresh all data (orders load first, then Stripe data in background)
                        </Tooltip>
                    }
                >
                    <Button 
                        bsStyle="warning" 
                        onClick={() => {
                            // Clear cache and reload
                            globalCache.stripeCustomers = null;
                            globalCache.stripeSubscriptions = null;
                            globalCache.lastFetchTime = null;
                            setUsingCachedData(false);
                            loadOrders();
                        }}
                        disabled={loading || loadingStripe}
                        style={{ padding: '6px 8px', marginRight: '8px' }}
                    >
                        <Glyphicon glyph="refresh" />
                    </Button>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id="returned-no-sub-toggle-tooltip">
                            {showReturnedNoSubscriptionOrders
                                ? "Hide returned orders with no subscription"
                                : `Show returned orders with no subscription (${orders.filter(isReturnedWithoutSubscription).length} hidden)`}
                        </Tooltip>
                    }
                >
                    <Button
                        bsStyle={showReturnedNoSubscriptionOrders ? "warning" : "default"}
                        onClick={() => setShowReturnedNoSubscriptionOrders(!showReturnedNoSubscriptionOrders)}
                        style={{ padding: '6px 8px', marginRight: '8px' }}
                    >
                        <Glyphicon glyph="inbox" />
                    </Button>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id="deleted-toggle-tooltip">
                            {showDeletedOrders ? "Hide deleted orders" : "Show deleted orders"}
                        </Tooltip>
                    }
                >
                    <Button 
                        bsStyle={showDeletedOrders ? "danger" : "default"}
                        onClick={() => setShowDeletedOrders(!showDeletedOrders)}
                        style={{ padding: '6px 8px' }}
                    >
                        <Glyphicon glyph={showDeletedOrders ? "eye-close" : "eye-open"} />
                    </Button>
                </OverlayTrigger>
            </Col>
            {/* <Col md={1}>
                
            </Col>
            <Col md={1}>
                
            </Col> */}
            {selectedOrders.size > 0 && (
                <Col md={2}>
                    <Button 
                        bsStyle="success" 
                        onClick={handleShippingConfirmation}
                        title={`Send shipping confirmation emails for ${selectedOrders.size} selected order(s)`}
                    >
                        <Glyphicon glyph="envelope" /> Send Shipping Confirmation ({selectedOrders.size})
                    </Button>
                </Col>
            )}
        </Row>
    );

    const renderPotentialOrders = () => {
        if (loadingPotential) {
            return (
                <div className="text-center">
                    <Glyphicon glyph="refresh" className="spinning" /> Loading potential orders...
                </div>
            );
        }

        if (potentialOrders.length === 0) {
            // return (
                // <div className="text-center">
                //     <p>No potential orders found. All caps with BUILD or ACTIVE status already have orders.</p>
                // </div>
            // );
            return null;
        }

        return (
            <div>
                <h4>Potential Orders ({potentialOrders.length})</h4>
                <p className="text-muted">Caps with BUILD or ACTIVE status that don't have orders yet</p>
                <Table responsive striped bordered hover className="orders-table">
                    <thead>
                        <tr>
                            <th>Cap Name</th>
                            <th>Status</th>
                            <th>Builder</th>
                            <th>Architecture</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {potentialOrders.map(cap => (
                            <tr key={cap.id}>
                                <td>{cap.name || 'Unnamed Cap'}</td>
                                <td>
                                    <Label bsStyle={cap.status === 'ACTIVE' ? 'success' : 'warning'}>
                                        {cap.status}
                                    </Label>
                                </td>
                                <td>{cap.builder?.name || 'Unknown Builder'}</td>
                                <td>
                                    <small>
                                        {cap.architecture?.endsWith('2CAM') ? 'JobSight' :
                                         cap.architecture?.endsWith('4CAM') ? 'PRO' :
                                         'Legacy'}
                                    </small>
                                </td>
                                <td>
                                    <Button 
                                        bsSize="sm" 
                                        bsStyle="success"
                                        onClick={() => createOrderFromCap(cap)}
                                        title="Create order from this cap"
                                    >
                                        <Glyphicon glyph="plus" /> Create Order
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    const renderOrdersTable = () => {
        const filteredOrders = getFilteredOrders();
        
        return (
            <div>
                            <div className="table-info">
                Showing {filteredOrders.length} of {orders.filter(order => order.status !== 'DELETE').length} active orders
                {!showReturnedNoSubscriptionOrders && orders.some(isReturnedWithoutSubscription) &&
                    ` (${orders.filter(isReturnedWithoutSubscription).length} returned, no subscription hidden)`}
                {showDeletedOrders && ` (including ${orders.filter(order => order.status === 'DELETE').length} deleted orders)`}
                {filterStatus === 'DELETE' && ` (showing ${filteredOrders.length} deleted orders)`}
                {filterStatus !== 'ALL' && filterStatus !== 'DELETE' && ` (filtered by order status: ${filterStatus})`}
                {filterBillingStatus !== 'ALL' && ` (filtered by billing status: ${filterBillingStatus === 'NO_SUBSCRIPTION' ? 'No Subscription' : filterBillingStatus})`}
                {searchTerm && ` (searching for "${searchTerm}")`}
                {sortBy !== 'deliveryDate' && ` (sorted by ${sortBy} ${sortDirection})`}
                {selectedOrders.size > 0 && ` (${selectedOrders.size} selected)`}
            </div>
                <Table responsive striped bordered hover className="orders-table">
                    <thead>
                        <tr>
                            <th>
                                <Checkbox
                                    checked={getFilteredOrders().length > 0 && selectedOrders.size === getFilteredOrders().length}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    title="Select all orders"
                                />
                            </th>
                            <th>Order</th>
                            <th>Camera</th>
                            <th 
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleSort('status')}
                                title="Click to sort by order status"
                            >
                                Order Status
                                {sortBy === 'status' && (
                                    <Glyphicon 
                                        glyph={sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'} 
                                        style={{ marginLeft: '5px' }}
                                    />
                                )}
                            </th>
                            <th 
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleSort('builder')}
                                title="Click to sort by builder"
                            >
                                Builder
                                {sortBy === 'builder' && (
                                    <Glyphicon 
                                        glyph={sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'} 
                                        style={{ marginLeft: '5px' }}
                                    />
                                )}
                            </th>
                            <th>Purchaser</th>
                            <th 
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleSort('deliveryDate')}
                                title="Click to sort by delivery date"
                            >
                                Delivery Date
                                {sortBy === 'deliveryDate' && (
                                    <Glyphicon 
                                        glyph={sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'} 
                                        style={{ marginLeft: '5px' }}
                                    />
                                )}
                            </th>
                            <th>Product</th>
                            <th>Monthly Rate</th>
                            <th>Billing Status</th>
                            <th>Expand</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOrders.map((order, index) => (
                            <React.Fragment key={`order-${order.orderId}-${index}`}>
                                <tr className={order.status === 'DELETE' ? 'deleted-order-row' : ''}>
                                    <td className="checkbox-cell">
                                        <Checkbox
                                            checked={selectedOrders.has(order.orderId)}
                                            onChange={(e) => handleOrderSelection(order.orderId, e.target.checked)}
                                            title={`Select order ${order.orderId}`}
                                        />
                                    </td>
                                    <td className="edit-button-cell">
                                        <div 
                                            className="edit-button"
                                            onClick={() => {
                                                setSelectedOrder(order);
                                                setShowEditModal(true);
                                            }}
                                            title="Click to edit order"
                                        >
                                            <Glyphicon glyph="edit" />
                                        </div>
                                    </td>
                                    <td className="camera-button-cell">
                                        {order.capId === null ? (
                                            <div className="camera-button no-camera">
                                                No Camera
                                            </div>
                                        ) : order.cap ? (
                                            <a 
                                                href={`/caps/${order.capId}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className={`camera-button ${order.cap.status === 'DECOMISSIONED' ? 'decommissioned' : ''}`}
                                                title={`View camera: ${order.cap.name}`}
                                            >
                                                {order.cap.name}
                                            </a>
                                        ) : (
                                            <div className="camera-button loading">
                                                Loading...
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <DropdownButton
                                            bsSize="sm"
                                            bsStyle={statusColors[order.status] || 'default'}
                                            title={order.status}
                                            id={`status-${order.id}`}
                                            className="orders-dropdown"
                                        >
                                            {orderStatuses.map(status => (
                                                <MenuItem
                                                    key={status}
                                                    eventKey={status}
                                                    onClick={() => handleStatusChange(order.orderId, status)}
                                                    active={order.status === status}
                                                    style={status === 'DELETE' ? { color: '#d9534f', fontWeight: 'bold' } : {}}
                                                >
                                                    {status}
                                                </MenuItem>
                                            ))}
                                        </DropdownButton>
                                    </td>
                                    <td className="builder-button-cell">
                                        {order.builder ? (
                                            <a 
                                                href={`/accesscontrol/${order.builderId}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="builder-button-admin"
                                                title={`View builder: ${order.builder.name}`}
                                            >
                                                {order.builder.name}
                                            </a>
                                        ) : (
                                            <div className="builder-button-admin loading">
                                                Loading...
                                            </div>
                                        )}
                                    </td>
                                    <td className="purchaser-button-cell">
                                        {order.purchaser ? (
                                            <a 
                                                href={`/accesscontrol/${order.builderId}/user/${order.purchaserId}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="purchaser-button"
                                                title={`View user: ${order.purchaser.firstName} ${order.purchaser.lastName}`}
                                            >
                                                {order.purchaser.firstName ? order.purchaser.firstName.toLowerCase() : order.purchaser.email} {order.purchaser.lastName ? order.purchaser.lastName.toLowerCase() : ''}
                                            </a>
                                        ) : (
                                            <div className="purchaser-button loading">
                                                Not Yet Set
                                            </div>
                                        )}
                                    </td>
                                    <td className="information-cell">
                                        {formatDate(order.deliveryDate)}
                                    </td>
                                    <td className="information-cell">
                                        {order.capId === null ? (
                                            <span className="text-muted">No Camera</span>
                                        ) : order.cap ? (
                                            <div>
                                                <small>
                                                    {order.cap.architecture?.endsWith('2CAM') ? 'JobSight' :
                                                     order.cap.architecture?.endsWith('4CAM') ? 'PRO' :
                                                     'Legacy'}
                                                </small>
                                            </div>
                                        ) : (
                                            <span>Loading...</span>
                                        )}
                                    </td>
                                    <td className="information-cell">
                                        {(() => {
                                            const monthlyRate = getMonthlyRate(order.subscription, order);
                                            if (React.isValidElement(monthlyRate)) {
                                                return monthlyRate;
                                            }
                                            return monthlyRate;
                                        })()}
                                    </td>
                                    <td className="billing-status-cell">
                                        {(() => {
                                            const billingStatus = getBillingStatus(order.subscription);
                                            if (React.isValidElement(billingStatus)) {
                                                return billingStatus;
                                            }
                                            return (
                                                <Label bsStyle={
                                                    billingStatus.includes('ACTIVE') ? 'success' : 
                                                    billingStatus.includes('TRIALING') ? 'info' : 
                                                    'warning'
                                                }>
                                                    {billingStatus}
                                                </Label>
                                            );
                                        })()}
                                    </td>
                                    <td className="expand-button-cell">
                                        <Button 
                                            bsSize="xs"
                                            className="expand-button"
                                            onClick={() => toggleOrderExpansion(order.orderId)}
                                            title={expandedOrders.has(order.orderId) ? "Collapse details" : "Expand details"}
                                        >
                                            <Glyphicon glyph={expandedOrders.has(order.orderId) ? "chevron-up" : "chevron-down"} />
                                        </Button>
                                    </td>
                                </tr>
                                {expandedOrders.has(order.orderId) && (
                                    <tr key={`expanded-row-${order.orderId}-${index}`} className="expanded-row">
                                        <td colSpan="11" className="expanded-row-content">
                                            <Row>
                                                <Col md={6}>
                                                    <div className="expanded-row-item">
                                                        <strong>Order ID:</strong>
                                                        <span className="expanded-row-monospace">
                                                            {order.orderId}
                                                        </span>
                                                        <CopyToClipboard text={order.orderId?.toString() || ''}>
                                                            <Button bsSize="xs" className="expanded-row-button">
                                                                <Glyphicon glyph="copy" />
                                                            </Button>
                                                        </CopyToClipboard>
                                                    </div>
                                                    <div className="expanded-row-item">
                                                        <strong>City:</strong>
                                                        <span className="expanded-row-label">
                                                            {getShippingCity(order.stripeCustomer)}
                                                        </span>
                                                    </div>
                                                    <div className="expanded-row-item">
                                                        <strong>Delivery Date:</strong>
                                                        <span className="expanded-row-label">
                                                            {formatDate(order.deliveryDate)}
                                                        </span>
                                                    </div>
                                                    <div className="expanded-row-item">
                                                        <strong>Order Date:</strong>
                                                        <span className="expanded-row-label">
                                                            {formatDate(order.addDate)}
                                                        </span>
                                                    </div>
                                                    <div className="expanded-row-item">
                                                        <strong>PO Number:</strong>
                                                        <span className="expanded-row-label">
                                                            {order.poNumber || 'N/A'}
                                                        </span>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="expanded-row-item">
                                                        <strong>Tracking:</strong>
                                                        {editingTracking === order.orderId ? (
                                                            <div className="expanded-row-tracking-container">
                                                                <FormControl
                                                                    type="text"
                                                                    value={trackingEditValue}
                                                                    onChange={(e) => setTrackingEditValue(e.target.value)}
                                                                    placeholder="Enter tracking number"
                                                                    style={{ display: 'inline-block', width: '150px', marginRight: '5px' }}
                                                                />
                                                                <Button 
                                                                    bsSize="xs" 
                                                                    bsStyle="success"
                                                                    onClick={() => handleTrackingUpdate(order.orderId, trackingEditValue)}
                                                                    style={{ marginRight: '5px' }}
                                                                >
                                                                    <Glyphicon glyph="ok" />
                                                                </Button>
                                                                <Button 
                                                                    bsSize="xs" 
                                                                    bsStyle="default"
                                                                    onClick={cancelTrackingEdit}
                                                                >
                                                                    <Glyphicon glyph="remove" />
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <div className="expanded-row-tracking-container">
                                                                {order.cap?.tracking ? (
                                                                    <>
                                                                        <a 
                                                                            href={`https://www.fedex.com/fedextrack/?trknbr=${order.cap.tracking}`}
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className="tracking-fedex-button"
                                                                        >
                                                                            {order.cap.tracking}
                                                                        </a>
                                                                        <CopyToClipboard text={order.cap.tracking}>
                                                                            <div className="tracking-copy-button expanded-row-tracking-copy">
                                                                                <Glyphicon glyph="copy" />
                                                                            </div>
                                                                        </CopyToClipboard>
                                                                        <Button 
                                                                            bsSize="xs" 
                                                                            bsStyle="info"
                                                                            onClick={() => startTrackingEdit(order.orderId, order.cap.tracking)}
                                                                            style={{ marginLeft: '5px' }}
                                                                            title="Edit tracking number"
                                                                        >
                                                                            <Glyphicon glyph="pencil" />
                                                                        </Button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <span className="expanded-row-na">N/A</span>
                                                                        <Button 
                                                                            bsSize="xs" 
                                                                            bsStyle="info"
                                                                            onClick={() => startTrackingEdit(order.orderId, '')}
                                                                            style={{ marginLeft: '5px' }}
                                                                            title="Add tracking number"
                                                                        >
                                                                            <Glyphicon glyph="plus" />
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="expanded-row-item">
                                                        <strong>Camera Health:</strong>
                                                        <span className="expanded-row-label">
                                                            {order.capId === null ? (
                                                                <Label bsStyle="default">
                                                                    No Camera
                                                                </Label>
                                                            ) : (
                                                                (() => {
                                                                    const status = getCameraStatus(order.cap);
                                                                    return (
                                                                        <Label bsStyle={status.style}>
                                                                            {status.text}
                                                                        </Label>
                                                                    );
                                                                })()
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="expanded-row-item">
                                                        <strong>Camera Status:</strong>
                                                        <span className="expanded-row-label">
                                                            {order.cap?.status || 'No Camera Assigned'}
                                                        </span>
                                                    </div>
                                                    {order.capId && (
                                                        <div className="expanded-row-item">
                                                            <strong>Camera Actions:</strong>
                                                            <div className="expanded-row-tracking-container">
                                                                <Button 
                                                                    bsSize="xs" 
                                                                    bsStyle="danger"
                                                                    onClick={() => {
                                                                        if (window.confirm('Are you sure you want to remove the camera from this order? This action cannot be undone.')) {
                                                                            handleRemoveCapId(order.orderId);
                                                                        }
                                                                    }}
                                                                    title="Remove camera from this order"
                                                                >
                                                                    <Glyphicon glyph="remove" /> Remove Camera
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    // const renderOrdersTable = () => {
    //     const filteredOrders = getFilteredOrders();
        
    //     return (
    //         <div>
    //             <div style={{ marginBottom: '10px', color: '#666' }}>
    //                 Showing {filteredOrders.length} of {enrichedOrders.length} orders
    //                 {filterStatus !== 'ALL' && ` (filtered by ${filterStatus})`}
    //                 {searchTerm && ` (searching for "${searchTerm}")`}
    //             </div>
    //             <Table responsive striped bordered hover className="orders-table">
    //                 <thead>
    //                     <tr>
    //                         <th>Order</th>
    //                         <th>Camera</th>
    //                         <th>Camera Status</th>
    //                         <th>Builder</th>
    //                         <th>Purchaser</th>
    //                         <th>Order Status</th>
    //                         <th>Product</th>
    //                         <th>City</th>
    //                         <th>Delivery Date</th>
    //                         <th>Order Date</th>
    //                         <th>Monthly Rate</th>
    //                         <th>Billing Status</th>
    //                         <th>Tracking</th>
    //                     </tr>
    //                 </thead>
    //                 <tbody>
    //                     {filteredOrders.map(order => (
    //                         <tr key={order.orderId}>
    //                             <td className="edit-button-cell">
    //                                 <div 
    //                                     className="edit-button"
    //                                     onClick={() => {
    //                                         setSelectedOrder(order);
    //                                         setShowEditModal(true);
    //                                     }}
    //                                     title="Click to edit order"
    //                                 >
    //                                     <Glyphicon glyph="edit" />
    //                                 </div>
    //                             </td>
    //                             <td className="camera-button-cell">
    //                                 {order.cap ? (
    //                                     <a 
    //                                         href={`/caps/${order.capId}`} 
    //                                         target="_blank" 
    //                                         rel="noopener noreferrer"
    //                                         className="camera-button"
    //                                         title={`View camera: ${order.cap.name}`}
    //                                     >
    //                                         {order.cap.name}
    //                                     </a>
    //                                 ) : (
    //                                     <div className="camera-button" style={{ backgroundColor: '#95a5a6', borderColor: '#7f8c8d', cursor: 'default', pointerEvents: 'none' }}>
    //                                         Loading...
    //                                     </div>
    //                                 )}
    //                             </td>
    //                             <td className="information-cell">
    //                                 {(() => {
    //                                     const status = getCameraStatus(order.cap);
    //                                     return (
    //                                         <Label bsStyle={status.style}>
    //                                             {status.text}
    //                                         </Label>
    //                                     );
    //                                 })()}
    //                             </td>
    //                             <td className="builder-button-cell">
    //                                 {order.builder ? (
    //                                     <a 
    //                                         href={`/accesscontrol/${order.builderId}`} 
    //                                         target="_blank" 
    //                                         rel="noopener noreferrer"
    //                                         className="builder-button-admin"
    //                                         title={`View builder: ${order.builder.name}`}
    //                                     >
    //                                         {order.builder.name}
    //                                     </a>
    //                                 ) : (
    //                                     <div className="builder-button-admin" style={{ backgroundColor: '#95a5a6', borderColor: '#7f8c8d', cursor: 'default', pointerEvents: 'none' }}>
    //                                         Loading...
    //                                     </div>
    //                                 )}
    //                             </td>
    //                             <td className="purchaser-button-cell">
    //                                 {order.purchaser ? (
    //                                     <a 
    //                                         href={`/accesscontrol/${order.builderId}/user/${order.purchaserId}`} 
    //                                         target="_blank" 
    //                                         rel="noopener noreferrer"
    //                                         className="purchaser-button"
    //                                         title={`View user: ${order.purchaser.firstName} ${order.purchaser.lastName}`}
    //                                     >
    //                                         {order.purchaser.firstName} {order.purchaser.lastName}
    //                                     </a>
    //                                 ) : (
    //                                     <div className="purchaser-button" style={{ backgroundColor: '#95a5a6', borderColor: '#7f8c8d', cursor: 'default', pointerEvents: 'none' }}>
    //                                         Loading...
    //                                     </div>
    //                                 )}
    //                             </td>
    //                             <td>
    //                                 <DropdownButton
    //                                     bsSize="sm"
    //                                     bsStyle={statusColors[order.status] || 'default'}
    //                                     title={
    //                                         order.status
    //                                     }
    //                                     id={`status-${order.id}`}
    //                                     className="orders-dropdown"
    //                                 >
    //                                     {orderStatuses.map(status => (
    //                                         <MenuItem
    //                                             key={status}
    //                                             eventKey={status}
    //                                             onClick={() => handleStatusChange(order.orderId, status)}
    //                                             active={order.status === status}
    //                                         >
    //                                             {status}
    //                                         </MenuItem>
    //                                     ))}
    //                                 </DropdownButton>
    //                             </td>
    //                             <td className="information-cell">
    //                                                             {order.cap ? (
    //                             <div>
    //                                 <small>
    //                                     {order.cap.architecture?.endsWith('2CAM') ? 'JobSight' :
    //                                      order.cap.architecture?.endsWith('4CAM') ? 'PRO' :
    //                                      'Legacy'}
    //                                 </small>
    //                             </div>
    //                         ) : (
    //                             <span>Loading...</span>
    //                         )}
    //                             </td>
    //                             <td className="information-cell">
    //                                 {getShippingCity(order.stripeCustomer)}
    //                             </td>
    //                             <td className="information-cell">
    //                                 {formatDate(order.deliveryDate)}
    //                             </td>
    //                             <td className="information-cell">
    //                                 {formatDate(order.addDate)}
    //                             </td>
    //                             <td className="information-cell">
    //                                 {getMonthlyRate(order.subscription)}
    //                             </td>
    //                             <td className="billing-status-cell">
    //                                 <Label bsStyle={
    //                                     getBillingStatus(order.subscription).includes('ACTIVE') ? 'success' : 
    //                                     getBillingStatus(order.subscription).includes('TRIALING') ? 'info' : 
    //                                     'warning'
    //                                 }>
    //                                     {getBillingStatus(order.subscription)}
    //                                 </Label>
    //                             </td>
    //                             <td className="tracking-button-cell">
    //                                 {order.cap?.tracking ? (
    //                                     <div className="tracking-buttons-container">
    //                                         <a 
    //                                             href={`https://www.fedex.com/fedextrack/?trknbr=${order.cap.tracking}`}
    //                                             target="_blank" 
    //                                             rel="noopener noreferrer"
    //                                             className="tracking-fedex-button"
    //                                             title={`Track package: ${order.cap.tracking}`}
    //                                         >
    //                                             {order.cap.tracking}
    //                                         </a>
    //                                         <CopyToClipboard text={order.cap.tracking}>
    //                                             <div className="tracking-copy-button" title="Copy tracking number">
    //                                                 <Glyphicon glyph="copy" />
    //                                             </div>
    //                                         </CopyToClipboard>
    //                                     </div>
    //                                 ) : (
    //                                     <div className="tracking-buttons-container">
    //                                         <div className="tracking-fedex-button" style={{ backgroundColor: '#95a5a6', borderColor: '#7f8c8d', cursor: 'default', pointerEvents: 'none' }}>
    //                                             N/A
    //                                         </div>
    //                                         <div className="tracking-copy-button" style={{ backgroundColor: '#95a5a6', borderColor: '#7f8c8d', cursor: 'default', pointerEvents: 'none' }}>
    //                                             <Glyphicon glyph="copy" />
    //                                         </div>
    //                                     </div>
    //                                 )}
    //                             </td>
    //                         </tr>
    //                     ))}
    //                 </tbody>
    //             </Table>
    //         </div>  
    //     )
    // }

    const adminOrdersHeader = (
        <header className="admin-header">
            <nav className="brand-breadcrumb">
                <Link to="/admin">Admin</Link>
                <span className="brand-breadcrumb-sep">/</span>
                <span className="brand-breadcrumb-current">Orders</span>
            </nav>
            <h1 className="admin-title">Orders Management</h1>
        </header>
    );

    if (loading) {
        return (
            <div className="orders-container admin-page">
            {adminOrdersHeader}
            <div className="text-center loading-container">
                <Glyphicon glyph="refresh" className="spinning loading-icon" />
                <p>Loading orders...</p>
            </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="orders-container admin-page">
                {adminOrdersHeader}
                <div className="alert alert-danger">{error}</div>
                <Button className="btn-brand-outline btn-compact" onClick={loadOrders}>Retry</Button>
            </div>
        );
    }

    return (
        <div className="orders-container admin-page">
            {adminOrdersHeader}
            {renderStats()}
            
            {renderFilters()}
            
            {/* Potential Orders Section */}
            <div style={{ marginBottom: '30px' }}>
                {renderPotentialOrders()}
            </div>
            
            {Object.keys(allStripeCustomers).length === 0 && !loading && (
                <div className="alert alert-warning">
                    <Glyphicon glyph="warning-sign" /> No Stripe customer data available
                </div>
            )}
            
            {Object.keys(allStripeSubscriptions).length === 0 && !loading && (
                <div className="alert alert-warning">
                    <Glyphicon glyph="warning-sign" /> No Stripe subscription data available
                </div>
            )}
            
            {loadingStripe && (
                <div className="alert alert-info">
                    <Glyphicon glyph="refresh" className="spinning" /> Loading billing data in background...
                </div>
            )}
            
            {usingCachedData && globalCache.lastFetchTime && !loadingStripe && (
                <div className="alert alert-success cached-data-alert">
                    <Glyphicon glyph="ok" /> Using cached Stripe data 
                    (last updated: {new Date(globalCache.lastFetchTime).toLocaleTimeString()})
                </div>
            )}
            
            {orders.length === 0 && !loading ? (
                <div className="text-center empty-state-container">
                    <Glyphicon glyph="shopping-cart" className="empty-state-icon" />
                    <h3>No Orders Found</h3>
                    <p>There are no orders in the system yet.</p>
                </div>
            ) : (
                renderOrdersTable()
            )}
            
            <OrderEditModal
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setSelectedOrder(null);
                }}
                order={selectedOrder}
                setOrder={setSelectedOrder}
                onUpdate={(updatedOrder) => {
                    console.log('Modal onUpdate called with:', updatedOrder);
                    if (updatedOrder) {
                        // If we have the updated order data, use efficient single update
                        console.log('Calling updateSingleOrder with:', updatedOrder);
                        updateSingleOrder(updatedOrder);
                        setSelectedOrder(updatedOrder);
                    } else {
                        // Fallback to full reload if no updated data provided
                        console.log('No updated order data, falling back to loadOrders');
                        loadOrders();
                    }
                }}
            />

            <ShippingConfirmationModal
                show={showShippingModal}
                onHide={() => setShowShippingModal(false)}
                orders={orders.filter(order => selectedOrders.has(order.orderId))}
                onSent={() => setSelectedOrders(new Set())}
            />
        </div>
    );
}
